import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';

const FoodBulkActionModal = ({
    isOpen,
    onClose,
    onConfirm,
    selectedCount,
    validDishTypes = [],
    temperatureOptions = ["Cold", "Room Temperature", "Warm", "Hot"]
}) => {
    const [activeCategory, setActiveCategory] = useState('Status');
    const [selectedAction, setSelectedAction] = useState('');
    const [actionValue, setActionValue] = useState(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    if (!isOpen) return null;

    const categories = [
        { name: 'Status', actions: ['Set Status → Active', 'Set Status → Inactive', 'Archive Dishes (trash / soft delete)'] },
        { name: 'Substitutions', actions: ['Toggle “Can you make substitutions for this item?”', 'Set to Yes', 'Set to No', 'Add / Update Substitution Notes (bulk)'] },
        { name: 'Notes', actions: ['Add Notes (append to existing notes)', 'Replace Notes', 'Clear Notes'] },
        { name: 'Cross-Contact', actions: ['Set Cross-Contact Yes', 'Set Cross-Contact No'] },
        { name: 'Ingredients', actions: ['Add Ingredient(s)', 'Remove Ingredient(s)', 'Replace Ingredient List'] },
        { name: 'Allergens', actions: ['Add Allergen(s)', 'Remove Allergen(s)', 'Replace Allergen List'] },
        { name: 'Health', actions: ['Add Health Restriction(s)', 'Remove Health Restriction(s)', 'Replace Health Restriction List'] },
        { name: 'Belief', actions: ['Add Belief Accommodation(s)', 'Remove Belief Accommodation(s)', 'Replace Belief Accommodation List'] },
        { name: 'Lifestyle', actions: ['Add Lifestyle Accommodation(s)', 'Remove Lifestyle Accommodation(s)', 'Replace Lifestyle Accommodation List'] },
        { name: 'Accommodations', actions: ['Add Accommodation(s)', 'Remove Accommodation(s)', 'Replace Accommodation List'] },
        { name: 'Menu Structure', actions: ['Change Dish Type', 'Change Temperature'] },
        { name: 'Pricing', actions: ['Set Price'] },
    ];

    const allergenOptions = ['Milk', 'Eggs', 'Fish', 'Shellfish', 'Tree Nuts', 'Peanuts', 'Wheat', 'Soy', 'Sesame', 'None'];
    const healthOptions = ['Gluten', 'Dairy', 'Egg', 'Soy', 'Nut', 'Shellfish', 'Fish', 'Alcohol', 'None'];
    const beliefOptions = ['Kosher', 'Halal', 'None'];
    const lifestyleOptions = ['Vegetarian', 'Vegan', 'Pescatarian', 'None'];
    const accommodationOptions = ['Vegan', 'Vegetarian', 'Gluten-Free', 'Dairy-Free', 'Nut-Free', 'Soy-Free', 'Sesame-Free', 'None'];

    const handleActionSelect = (action) => {
        setSelectedAction(action);
        setActionValue(null);
        setIsConfirming(false);
    };

    const handleConfirm = async () => {
        if (!isConfirming) {
            setIsConfirming(true);
        } else {
            setIsLoading(true);
            try {
                await onConfirm(selectedAction, actionValue);
                onClose();
            } catch (error) {
                console.error("Bulk action failed:", error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const renderActionInput = () => {
        switch (selectedAction) {
            case 'Add / Update Substitution Notes (bulk)':
            case 'Add Notes (append to existing notes)':
            case 'Replace Notes':
                return (
                    <textarea
                        className="w-full p-2 border border-gray-300 rounded-md mt-4 h-24"
                        placeholder="Enter notes..."
                        value={actionValue || ''}
                        onChange={(e) => setActionValue(e.target.value)}
                    />
                );
            case 'Add Ingredient(s)':
            case 'Remove Ingredient(s)':
            case 'Replace Ingredient List':
                return (
                    <div className="mt-4">
                        <p className="text-sm text-gray-500 mb-2">Enter ingredients separated by commas:</p>
                        <input
                            type="text"
                            className="w-full p-2 border border-gray-300 rounded-md"
                            placeholder="e.g. Tomato, Cheese, Basil"
                            value={actionValue || ''}
                            onChange={(e) => setActionValue(e.target.value)}
                        />
                    </div>
                );
            case 'Add Allergen(s)':
            case 'Remove Allergen(s)':
            case 'Replace Allergen List':
                return (
                    <div className="grid grid-cols-2 gap-2 mt-4">
                        {allergenOptions.map(option => (
                            <label key={option} className="flex items-center gap-2 text-sm p-2 border border-gray-100 rounded hover:bg-gray-50 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={(actionValue || []).includes(option)}
                                    onChange={(e) => {
                                        const current = actionValue || [];
                                        if (e.target.checked) setActionValue([...current, option]);
                                        else setActionValue(current.filter(i => i !== option));
                                    }}
                                />
                                {option}
                            </label>
                        ))}
                    </div>
                );
            case 'Add Health Restriction(s)':
            case 'Remove Health Restriction(s)':
            case 'Replace Health Restriction List':
                return (
                    <div className="grid grid-cols-2 gap-2 mt-4">
                        {healthOptions.map(option => (
                            <label key={option} className="flex items-center gap-2 text-sm p-2 border border-gray-100 rounded hover:bg-gray-50 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={(actionValue || []).includes(option)}
                                    onChange={(e) => {
                                        const current = actionValue || [];
                                        if (e.target.checked) setActionValue([...current, option]);
                                        else setActionValue(current.filter(i => i !== option));
                                    }}
                                />
                                {option}
                            </label>
                        ))}
                    </div>
                );
            case 'Add Belief Accommodation(s)':
            case 'Remove Belief Accommodation(s)':
            case 'Replace Belief Accommodation List':
                return (
                    <div className="grid grid-cols-2 gap-2 mt-4">
                        {beliefOptions.map(option => (
                            <label key={option} className="flex items-center gap-2 text-sm p-2 border border-gray-100 rounded hover:bg-gray-50 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={(actionValue || []).includes(option)}
                                    onChange={(e) => {
                                        const current = actionValue || [];
                                        if (e.target.checked) setActionValue([...current, option]);
                                        else setActionValue(current.filter(i => i !== option));
                                    }}
                                />
                                {option}
                            </label>
                        ))}
                    </div>
                );
            case 'Add Lifestyle Accommodation(s)':
            case 'Remove Lifestyle Accommodation(s)':
            case 'Replace Lifestyle Accommodation List':
                return (
                    <div className="grid grid-cols-2 gap-2 mt-4">
                        {lifestyleOptions.map(option => (
                            <label key={option} className="flex items-center gap-2 text-sm p-2 border border-gray-100 rounded hover:bg-gray-50 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={(actionValue || []).includes(option)}
                                    onChange={(e) => {
                                        const current = actionValue || [];
                                        if (e.target.checked) setActionValue([...current, option]);
                                        else setActionValue(current.filter(i => i !== option));
                                    }}
                                />
                                {option}
                            </label>
                        ))}
                    </div>
                );
            case 'Add Accommodation(s)':
            case 'Remove Accommodation(s)':
            case 'Replace Accommodation List':
                return (
                    <div className="grid grid-cols-2 gap-2 mt-4">
                        {accommodationOptions.map(option => (
                            <label key={option} className="flex items-center gap-2 text-sm p-2 border border-gray-100 rounded hover:bg-gray-50 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={(actionValue || []).includes(option)}
                                    onChange={(e) => {
                                        const current = actionValue || [];
                                        if (e.target.checked) setActionValue([...current, option]);
                                        else setActionValue(current.filter(i => i !== option));
                                    }}
                                />
                                {option}
                            </label>
                        ))}
                    </div>
                );
            case 'Change Dish Type':
                return (
                    <select
                        className="w-full p-2 border border-gray-300 rounded-md mt-4"
                        value={actionValue || ''}
                        onChange={(e) => setActionValue(e.target.value)}
                    >
                        <option value="">Select Dish Type</option>
                        {validDishTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>
                );
            case 'Change Temperature':
                return (
                    <select
                        className="w-full p-2 border border-gray-300 rounded-md mt-4"
                        value={actionValue || ''}
                        onChange={(e) => setActionValue(e.target.value)}
                    >
                        <option value="">Select Temperature</option>
                        {temperatureOptions.map(temp => (
                            <option key={temp} value={temp}>{temp}</option>
                        ))}
                    </select>
                );
            case 'Set Price':
                return (
                    <div className="mt-4">
                        <label className="text-sm text-gray-500 block mb-1">Set Price (numbers only):</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                            <input
                                type="number"
                                step="0.01"
                                className="w-full p-2 pl-7 border border-gray-300 rounded-md"
                                placeholder="0.00"
                                value={actionValue || ''}
                                onChange={(e) => setActionValue(e.target.value)}
                            />
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
                    <div>
                        <h2 className="text-xl font-bold text-[#881337]">Bulk Actions - Food Menu Items</h2>
                        <p className="text-sm text-gray-500 font-medium">{selectedCount} dishes selected</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <X size={20} className="text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto flex">
                    {/* Categories Sidebar */}
                    <div className="w-48 bg-gray-50 border-r border-gray-100">
                        {categories.map(cat => (
                            <button
                                key={cat.name}
                                onClick={() => {
                                    setActiveCategory(cat.name);
                                    setSelectedAction('');
                                    setActionValue(null);
                                    setIsConfirming(false);
                                }}
                                className={`w-full text-left px-4 py-3 text-sm font-semibold transition-colors ${activeCategory === cat.name
                                    ? 'bg-white text-[#881337] border-r-2 border-[#881337]'
                                    : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </div>

                    {/* Actions Pane */}
                    <div className="flex-1 p-6">
                        {!selectedAction ? (
                            <div className="space-y-2">
                                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">
                                    Select an action for {activeCategory}
                                </h3>
                                {categories.find(c => c.name === activeCategory)?.actions.map(action => (
                                    <button
                                        key={action}
                                        onClick={() => handleActionSelect(action)}
                                        className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-[#881337]/30 hover:bg-[#881337]/5 transition-all group"
                                    >
                                        <span className="text-sm font-semibold text-gray-700 group-hover:text-[#881337]">{action}</span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <button
                                    onClick={() => {
                                        setSelectedAction('');
                                        setIsConfirming(false);
                                    }}
                                    className="text-xs font-bold text-[#881337] hover:underline mb-2 flex items-center gap-1"
                                >
                                    ← Back to {activeCategory}
                                </button>
                                <h3 className="text-lg font-bold text-gray-900">{selectedAction}</h3>

                                {renderActionInput()}

                                {isConfirming && (
                                    <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-xl flex gap-3">
                                        <AlertCircle className="text-orange-500 shrink-0" size={24} />
                                        <div>
                                            <h4 className="font-bold text-orange-900">Are you sure?</h4>
                                            <p className="text-sm text-orange-800">
                                                This will apply "{selectedAction}" to {selectedCount} selected dishes. This action cannot be easily undone.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 rounded-lg font-bold text-sm text-gray-500 hover:bg-gray-100 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={!selectedAction || (selectedAction === 'Set Price' && !actionValue) || isLoading}
                        onClick={handleConfirm}
                        className={`px-8 py-2 rounded-lg font-bold text-sm shadow-md transition-all ${!selectedAction
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : isConfirming
                                ? 'bg-orange-500 text-white hover:bg-orange-600'
                                : 'bg-[#881337] text-white hover:bg-[#70102d]'
                            }`}
                    >
                        {isLoading ? (
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                <span>Applying...</span>
                            </div>
                        ) : isConfirming ? 'Confirm Apply' : 'Apply Action'}
                    </button>
                </div>
            </div>
        </div >
    );
};

export default FoodBulkActionModal;
